import { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Icon,
  MenuItem,
  Select,
  styled,
} from '@mui/material';
import projectDropdownArrow from '../../assets/project-dropdown-arrow.svg';
import { UIColors } from '../../constants/UIConstants';
import { useAuth } from '../../contexts/AuthContext';
import {
  COMPACT_DISPLAY_NAME_MAX_CHARS,
  formatPlatformRoleLabel,
  isPlatformAdminUser,
  truncateDisplayName,
  UserOrganizationMembership,
} from '../../types/onboarding';
import { pxToRem } from '../../utils/getFontValue';

/** Caps switcher width at ~20 chars plus role badge; shorter names shrink to fit. */
const orgSwitcherMaxWidth = `calc(${COMPACT_DISPLAY_NAME_MAX_CHARS}ch + ${pxToRem(112)})`;

const StyledOrgSelect = styled(Select)(({ theme }) => ({
  width: 'fit-content',
  maxWidth: orgSwitcherMaxWidth,
  flexShrink: 0,
  height: pxToRem(40),
  color: UIColors.primary.navyBlue,
  backgroundColor: theme.palette.background.paper,
  '&.MuiInputBase-root': {
    width: 'fit-content',
    maxWidth: orgSwitcherMaxWidth,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: 0,
    width: 'auto',
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8),
  },
}));

const StyledDropdownIcon = styled(Icon)({
  borderRadius: pxToRem(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledReadOnlyOrg = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: orgSwitcherMaxWidth,
  height: pxToRem(40),
  padding: `0 ${pxToRem(12)}`,
  borderRadius: pxToRem(4),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

function OrgDropdownIcon(props: Record<string, unknown>) {
  return (
    <StyledDropdownIcon {...props}>
      <img alt="" src={projectDropdownArrow} />
    </StyledDropdownIcon>
  );
}

function AppHeaderOrgDropdownIcon(props: Record<string, unknown>) {
  return (
    <KeyboardArrowDownIcon
      {...props}
      sx={{ fontSize: 20, color: 'inherit' }}
    />
  );
}

type OrgSwitcherAppearance = 'default' | 'appHeader';

interface OrgMembershipLabelProps {
  organizationName: string;
  orgRole: string;
  appearance?: OrgSwitcherAppearance;
  inMenu?: boolean;
}

/** Org name (truncated) + role for switcher trigger and menu rows. */
function OrgMembershipLabel({
  organizationName,
  orgRole,
  appearance = 'default',
  inMenu = false,
}: OrgMembershipLabelProps) {
  const roleLabel = formatPlatformRoleLabel(orgRole);
  const { display, full, truncated } = truncateDisplayName(organizationName);
  const nameTitle = truncated ? full : undefined;
  const isHeader = appearance === 'appHeader';

  const roleClass = isHeader
    ? 'org-menu-role shrink-0 text-xs font-medium text-white/80'
    : 'org-menu-role shrink-0 text-xs font-medium text-gray-500';

  if (inMenu) {
    return (
      <span className="flex w-full min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate" title={nameTitle}>
          {display}
        </span>
        <span className={roleClass}>{roleLabel}</span>
      </span>
    );
  }

  return (
    <span
      className={
        isHeader
          ? 'flex w-fit max-w-full items-center gap-2 text-sm font-semibold text-white'
          : 'flex w-fit max-w-full items-center gap-2'
      }
    >
      <span
        className={
          isHeader
            ? 'shrink-0 whitespace-nowrap'
            : 'shrink-0 whitespace-nowrap text-sm font-semibold'
        }
        style={isHeader ? undefined : { color: UIColors.primary.navyBlue }}
        title={nameTitle}
      >
        {display}
      </span>
      <span className={roleClass}>{roleLabel}</span>
    </span>
  );
}

const appHeaderSelectSx = {
  width: 'fit-content',
  maxWidth: orgSwitcherMaxWidth,
  flexShrink: 0,
  height: pxToRem(40),
  borderRadius: pxToRem(8),
  color: '#FFFFFF',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '&.MuiInputBase-root': {
    width: 'fit-content',
    maxWidth: orgSwitcherMaxWidth,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.35)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.55)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: 0,
    color: '#FFFFFF',
    fontSize: pxToRem(14),
    fontWeight: 600,
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8),
    paddingRight: `${pxToRem(32)} !important`,
  },
  '& .MuiSelect-icon': {
    color: '#FFFFFF',
    right: pxToRem(8),
  },
};

function orgSwitcherMenuPaperProps(menuWidth?: number) {
  const base = {
    sx: {
      color: UIColors.primary.navyBlue,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      '& .MuiMenuItem-root': { overflow: 'hidden', maxWidth: '100%' },
    },
  };

  if (menuWidth == null) {
    return base;
  }

  return {
    sx: {
      ...base.sx,
      width: menuWidth,
      minWidth: menuWidth,
      maxWidth: menuWidth,
    },
    style: {
      width: menuWidth,
      minWidth: menuWidth,
      maxWidth: menuWidth,
    },
  };
}

interface PlatformAdminBadgeProps {
  appearance?: OrgSwitcherAppearance;
}

export function PlatformAdminBadge({
  appearance = 'default',
}: PlatformAdminBadgeProps) {
  const className =
    appearance === 'appHeader'
      ? 'inline-flex h-10 items-center whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-3 text-sm font-medium text-white'
      : 'inline-flex h-10 items-center whitespace-nowrap rounded-lg border border-brand/30 bg-brand/5 px-3 text-sm font-medium text-brand';

  return (
    <div
      className={className}
      title="Global access. Pick organization on each form."
    >
      Admin
    </div>
  );
}

interface OrgSwitcherProps {
  appearance?: OrgSwitcherAppearance;
}

export function OrgSwitcher({ appearance = 'default' }: OrgSwitcherProps) {
  const {
    currentUser,
    memberships,
    switchOrganization,
    isLoading,
    isSwitchingOrganization,
    isOrphanOrgUser,
  } = useAuth();
  const isPlatformAdmin = isPlatformAdminUser(currentUser);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();

  const options = useMemo(() => {
    if (isOrphanOrgUser) return [];
    if (memberships.length > 0) return memberships;
    if (currentUser?.organization) {
      return [
        {
          organizationId: currentUser.activeOrganizationId ?? -1,
          organizationName: currentUser.organization,
          orgRole: currentUser.platformRole,
        },
      ];
    }
    return [];
  }, [memberships, currentUser, isOrphanOrgUser]);

  if (isPlatformAdmin) {
    return <PlatformAdminBadge appearance={appearance} />;
  }

  if (!currentUser || options.length === 0) {
    return null;
  }

  const activeMembership =
    options.find(
      (o: UserOrganizationMembership) =>
        o.organizationId === currentUser.activeOrganizationId
    ) ?? options[0];
  const activeOrgName =
    activeMembership?.organizationName || currentUser.organization || '';
  const activeOrgRole =
    activeMembership?.orgRole || currentUser.platformRole || 'developer';

  if (options.length <= 1) {
    if (appearance === 'appHeader') {
      return (
        <div
          className="inline-flex h-10 w-fit items-center rounded-lg border border-white/30 bg-white/10 px-3"
          style={{ maxWidth: orgSwitcherMaxWidth }}
        >
          <OrgMembershipLabel
            organizationName={activeOrgName}
            orgRole={activeOrgRole}
            appearance={appearance}
          />
        </div>
      );
    }

    return (
      <StyledReadOnlyOrg>
        <OrgMembershipLabel
          organizationName={activeOrgName}
          orgRole={activeOrgRole}
          appearance={appearance}
        />
      </StyledReadOnlyOrg>
    );
  }

  const activeId =
    currentUser.activeOrganizationId ?? options[0]?.organizationId ?? '';

  return (
    <StyledOrgSelect
      IconComponent={
        appearance === 'appHeader' ? AppHeaderOrgDropdownIcon : OrgDropdownIcon
      }
      value={activeId}
      variant="outlined"
      disabled={isLoading || isSwitchingOrganization}
      sx={appearance === 'appHeader' ? appHeaderSelectSx : undefined}
      onOpen={(event) => {
        setMenuWidth((event.currentTarget as HTMLElement).clientWidth);
      }}
      onClose={() => setMenuWidth(undefined)}
      onChange={async (event) => {
        const nextId = Number(event.target.value);
        if (
          isSwitchingOrganization ||
          !Number.isFinite(nextId) ||
          nextId <= 0 ||
          nextId === currentUser.activeOrganizationId
        ) {
          return;
        }
        try {
          await switchOrganization(nextId);
        } catch {
          // AuthContext surfaces switch failures via snackbar.
        }
      }}
      renderValue={() => (
        <OrgMembershipLabel
          organizationName={activeOrgName}
          orgRole={activeOrgRole}
          appearance={appearance}
        />
      )}
      MenuProps={{
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
        PaperProps: orgSwitcherMenuPaperProps(menuWidth),
        MenuListProps: { sx: { width: '100%', py: 0.5 } },
      }}
    >
      {options.map((org: UserOrganizationMembership) => (
        <MenuItem
          key={`${org.organizationId}-${org.organizationName}`}
          value={org.organizationId}
          sx={{ overflow: 'hidden', maxWidth: '100%' }}
        >
          <OrgMembershipLabel
            organizationName={org.organizationName}
            orgRole={org.orgRole}
            inMenu
          />
        </MenuItem>
      ))}
    </StyledOrgSelect>
  );
}

export default OrgSwitcher;
